/** A display-only projection of a caption view. Indices always address the source array. */
export type CaptionWindowEntry<T> = {index: number; caption: T};

export function projectCaptionViewWindow<T extends {startMs: number; endMs: number}>(
  captions: readonly T[],
  view: {
    timelineSourceAssetId?: string | null;
    captionStartInSeconds: number;
    durationInFrames: number;
    fps: number;
    captionEdgeHold?: {left?: boolean; right?: boolean};
  },
): CaptionWindowEntry<T>[] {
  const indexed = captions.map((caption, index) => ({index, caption}));
  // Only an explicit source-backed view has a cut window. Legacy assets keep
  // their original array and timing, including edge holds and long tails.
  if (!view.timelineSourceAssetId || !Number.isFinite(view.captionStartInSeconds) ||
      !Number.isFinite(view.durationInFrames) || view.durationInFrames <= 0 ||
      !Number.isFinite(view.fps) || view.fps <= 0) return indexed;

  const startMs = view.captionStartInSeconds * 1000;
  const endMs = startMs + view.durationInFrames / view.fps * 1000;
  const intersecting = indexed.flatMap(({index, caption}) => {
    if (!(caption.endMs > startMs && caption.startMs < endMs)) return [];
    return [{
      index,
      caption: {
        ...caption,
        startMs: Math.max(caption.startMs, startMs),
        endMs: Math.min(caption.endMs, endMs),
      },
    }];
  });
  if (intersecting.length > 0 || !view.captionEdgeHold || captions.length === 0) return intersecting;

  // An edge hold displays the first/last existing page even when its source
  // words are entirely outside the cut. Keep the old page input in that case;
  // captionEdgeFrames still owns extending that page across the item.
  const firstMs = Math.min(...captions.map((caption) => caption.startMs));
  const lastMs = Math.max(...captions.map((caption) => caption.endMs));
  return (view.captionEdgeHold.right && startMs >= lastMs) ||
    (view.captionEdgeHold.left && endMs <= firstMs)
    ? indexed
    : intersecting;
}
